import { WAKE_MAX_NAMES, type WakeHandle } from '@officehub/offline-sdk';

/**
 * Keep-warm loop (L-0868, Nick 2026-10-15: "anytime the app is open ... the module landing page warm ... every page on the
 * module's nav/subnav needs to be warm always when inside that module").
 *
 * WHY THESE NUMBERS (measured 2026-10-13, all-caller Cloud Run request logs, drops/BH18-L0868-KEEPWARM-DOD-2026-10-15.md):
 * a function goes cold 15 minutes after its last request, sharply (gap < 15 min: 12 cold of 2,941 user pre-flights;
 * gap >= 15 min: 155 of 208), and 205 of that day's 213 user cold waits hit someone whose app was already open. So the
 * loop ticks every KEEP_WARM_TICK_MS and re-knocks a name once KEEP_WARM_THROTTLE_MS has passed: every name in the set
 * is knocked again 9-11 minutes after its last knock, inside the 15-minute window with 4 minutes to spare.
 * Framework-neutral and clock-injected, so a test can drive a whole session on a fake clock.
 */
export const KEEP_WARM_TICK_MS = 2 * 60 * 1000;
export const KEEP_WARM_THROTTLE_MS = 9 * 60 * 1000;
/**
 * How long a HIDDEN tab keeps its small core set warm (smart-knock, 2026-10-29). A backgrounded billing tab used to go cold after 15 minutes and the
 * first click on tab-show met the cold server (finance-qboread 10-28: 3 of 3 misses had no knock in the prior 20 minutes). Browsers slow a hidden
 * tab's timers to about one a minute, which is still inside the 15-minute window. After this long hidden the tab is treated as abandoned and
 * nothing is knocked, so a tab left open overnight does not keep servers awake all night. The clock starts at the first tick that finds the tab hidden
 * (up to one tick, ~2 min, after the hide) and starts again whenever the loop restarts (module/page change, switch loading), so the cap is per loop run.
 */
export const KEEP_WARM_HIDDEN_MAX_MS = 2 * 60 * 60 * 1000;
/** Gap between two chunks of <= WAKE_MAX_NAMES names: a few OPTIONS at a time, never a burst. */
export const KEEP_WARM_CHUNK_GAP_MS = 2000;

export interface KeepWarmLoopOptions {
  /** The names to keep warm, read on every tick (de-duplicated here). */
  readonly names: () => readonly string[];
  /** Knocks one chunk (wakeCallables with KEEP_WARM_THROTTLE_MS); returns its handle so a stop can cancel unsent knocks. */
  readonly knock: (chunk: readonly string[]) => WakeHandle;
  /** False = the kill switch is off: one pass on start, no repeat (the one-shot behaviour from before L-0868). */
  readonly repeat: boolean;
  /** A hidden tab knocks only `hiddenNames` (none given, or `repeat` false = nothing while hidden); the caller ticks again when it is shown. */
  readonly isVisible: () => boolean;
  /** The small core set to keep warm while the tab is hidden, read on every hidden tick. Absent = a hidden tab knocks nothing. */
  readonly hiddenNames?: () => readonly string[];
  /** Stop knocking after the tab has been hidden this long (default KEEP_WARM_HIDDEN_MAX_MS). */
  readonly hiddenMaxMs?: number;
  /** Clock, injectable for tests. */
  readonly now?: () => number;
  /** Delay before the first pass (the app shell's own list goes first on app open). */
  readonly firstDelayMs?: number;
  readonly tickMs?: number;
  readonly chunkGapMs?: number;
}

export interface KeepWarmLoop {
  /** Knock now (tab shown, back online). No-op after stop(). */
  tick(): void;
  /** Cancel every pending chunk and unsent knock, and stop repeating. */
  stop(): void;
}

/** Splits a list into chunks of at most `size`, in order. Pure. */
export function chunk<T>(list: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
}

export function startKeepWarmLoop(opts: KeepWarmLoopOptions): KeepWarmLoop {
  const tickMs = opts.tickMs ?? KEEP_WARM_TICK_MS;
  const gapMs = opts.chunkGapMs ?? KEEP_WARM_CHUNK_GAP_MS;
  let stopped = false;
  let pending: ReturnType<typeof setTimeout>[] = [];
  let handles: WakeHandle[] = [];
  const now = opts.now ?? Date.now;
  const hiddenMaxMs = opts.hiddenMaxMs ?? KEEP_WARM_HIDDEN_MAX_MS;
  // When a tick first found the tab hidden (null while visible). A hidden tab's tick can lag the real hide by up to one tick.
  let hiddenSince: number | null = null;

  const clearPass = () => {
    pending.forEach(clearTimeout);
    handles.forEach((h) => h.cancel());
    pending = [];
    handles = [];
  };

  const tick = () => {
    if (stopped) return;
    const visible = opts.isVisible();
    if (visible) hiddenSince = null;
    else hiddenSince ??= now();
    // Kill switch off (repeat false) = no hidden-tab traffic at all, as before this change.
    if (!visible && (!opts.repeat || !opts.hiddenNames || now() - (hiddenSince ?? now()) > hiddenMaxMs)) return;
    // A new pass replaces the previous one: unsent chunks are dropped (cancel frees their throttle slots) and re-planned.
    clearPass();
    const unique = Array.from(new Set(visible ? opts.names() : opts.hiddenNames?.() ?? []));
    chunk(unique, WAKE_MAX_NAMES).forEach((names, i) => {
      pending.push(setTimeout(() => {
        if (!stopped) handles.push(opts.knock(names));
      }, i * gapMs));
    });
  };

  const first = setTimeout(tick, opts.firstDelayMs ?? 0);
  const every = opts.repeat ? setInterval(tick, tickMs) : null;

  return {
    tick,
    stop: () => {
      stopped = true;
      clearTimeout(first);
      if (every) clearInterval(every);
      clearPass();
    },
  };
}
